import { useEffect } from "react";

import {
  ExecutionTiersData,
  SupportedPublicPerformanceTierKey,
  useGetPerformanceTiers,
} from "shared/SelectPerformance/useGetPerformanceTiers";
import {
  getDefaultPerformanceKey as defaultGetDefaultPerformanceKey,
  GetDefaultPerformanceKey,
} from "shared/SelectPerformance/utils/getDefaultPerformanceKey";
import { useActiveContext, ActiveContext } from "shared/ContextSwitcher/store";
import {
  getDefaultPerformance_NoValidation,
  usePerformanceForQuery_NoValidation,
  useSetPerformanceForQuery,
  setPerformanceForQuery,
} from "./useQueryPerformanceState_store";

const freePerformance: SupportedPublicPerformanceTierKey = "free";

export function useQueryPerformanceState({
  queryId = "default",
  datasetId,
  getDefaultPerformanceKey = defaultGetDefaultPerformanceKey,
}: {
  queryId?: number | "default";
  datasetId: number | undefined;
  getDefaultPerformanceKey?: GetDefaultPerformanceKey;
}) {
  const activeContext = useActiveContext();
  const { performanceTiers } = useAvailablePerformanceTiersForDataset(
    activeContext,
    datasetId
  );

  const performance = usePerformanceForQuery_NoValidation(queryId);
  const setPerformance = useSetPerformanceForQuery(queryId);

  function isValid(
    performance: SupportedPublicPerformanceTierKey | undefined
  ): performance is SupportedPublicPerformanceTierKey {
    return performanceTiers.some(
      (tier): boolean =>
        tier.publicPerformanceTierKey === performance &&
        // If the tier is disabled then it's not valid
        !("disabled" in tier && tier.disabled)
    );
  }

  function getValidDefaultPerformance(): SupportedPublicPerformanceTierKey {
    const storedDefault = getDefaultPerformance_NoValidation();

    if (isValid(storedDefault)) {
      return storedDefault;
    }

    return (
      getDefaultPerformanceKey(activeContext, performanceTiers) ??
      freePerformance
    );
  }

  function getValidSelectedPerformance(): SupportedPublicPerformanceTierKey {
    if (isValid(performance)) {
      return performance;
    }

    return getValidDefaultPerformance();
  }

  function updateLastExecutedPerformance() {
    setPerformanceForQuery("default", getValidSelectedPerformance());
  }

  const canSelectPerformance = performanceTiers.length > 0;

  if (!canSelectPerformance) {
    return {
      canSelectPerformance,
    };
  } else {
    return {
      canSelectPerformance,
      performance: getValidSelectedPerformance(),
      setPerformance,
      updateLastExecutedPerformance,
      performanceTiers,
    };
  }
}

export function useAvailablePerformanceTiersForDataset(
  activeContext: ActiveContext | undefined,
  datasetId: number | undefined
) {
  const {
    data: performanceTiersData,
    error,
    loading,
  } = useGetPerformanceTiers(activeContext);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [error]);

  let performanceTiers: ExecutionTiersData[] = [];
  if (performanceTiersData && datasetId !== undefined) {
    performanceTiers = performanceTiersData[datasetId] ?? [];
  }

  return { loading, performanceTiers };
}
